import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import {
  Users, Briefcase, Clock, DollarSign, CheckSquare, Shield,
  TrendingUp, AlertCircle, Calendar, FileText, AlertTriangle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function StatCard({ icon: Icon, label, value, sub, color, onClick }) {
  return (
    <div
      className={`card ${onClick ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon size={22} className="text-white" />
        </div>
      </div>
    </div>
  );
}

const MANAGEMENT_ROLES = new Set(['Senior Partner', 'Partner', 'Admin']);
const BILLING_ROLES = new Set(['Accountant', 'Senior Partner', 'Partner', 'Admin']);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || '';

  useEffect(() => {
    dashboardAPI.get()
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const greeting = new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening';

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-96">
      <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  const isManagement = MANAGEMENT_ROLES.has(role);
  const isBilling = BILLING_ROLES.has(role);

  return (
    <div className="p-6">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Good {greeting}, {user?.first_name}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {isManagement
            ? "Here's your firm's performance overview."
            : isBilling
            ? "Here's your billing and financial summary."
            : "Here's your workload for today."}
        </p>
      </div>

      {/* Stats — management sees all 6; others see relevant subset */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {isManagement && (
          <StatCard icon={Users} label="Active Clients" value={data?.total_clients || 0} color="bg-blue-600" onClick={() => navigate('/clients')} />
        )}
        <StatCard icon={Briefcase} label="Open Cases" value={data?.open_cases || 0} color="bg-indigo-600" onClick={() => navigate('/cases')} />
        {isBilling && (
          <StatCard icon={DollarSign} label="Outstanding" value={`$${(data?.pending_invoices_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`} sub="Invoices" color="bg-amber-500" onClick={() => navigate('/invoices')} />
        )}
        <StatCard icon={Clock} label="Unbilled Hours" value={`${(data?.unbilled_hours || 0).toFixed(1)}h`} color="bg-emerald-600" onClick={() => navigate('/time-entries')} />
        <StatCard icon={CheckSquare} label="Tasks Due Today" value={data?.tasks_due_today || 0} color="bg-rose-500" onClick={() => navigate('/tasks')} />
        {isBilling && (
          <StatCard icon={Shield} label="Trust Balance" value={`$${(data?.trust_balance_total || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`} color="bg-purple-600" onClick={() => navigate('/trust')} />
        )}
        {isBilling && (
          <StatCard icon={AlertTriangle} label="Overdue Invoices" value={data?.overdue_invoices || 0} color="bg-red-600" onClick={() => navigate('/invoices')} />
        )}
      </div>

      {/* Management: charts + recent cases */}
      {isManagement && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Revenue — Last 6 Months</h3>
            {data?.monthly_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.monthly_revenue} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [`$${v.toLocaleString()}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No revenue data yet</div>
            )}
          </div>

          <div className="card">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Cases by Type</h3>
            {data?.case_type_breakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.case_type_breakdown} dataKey="count" nameKey="case_type" cx="50%" cy="50%" outerRadius={75}
                    label={({ case_type, percent }) => `${case_type} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {data.case_type_breakdown.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No cases yet</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Cases */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              {isManagement ? 'Recent Cases' : 'My Cases'}
            </h3>
            <button onClick={() => navigate('/cases')} className="text-blue-600 text-sm hover:underline">View all</button>
          </div>
          <div className="space-y-3">
            {data?.recent_cases?.length > 0 ? data.recent_cases.map(c => (
              <div key={c.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                onClick={() => navigate(`/cases/${c.id}`)}>
                <div>
                  <p className="text-sm font-medium text-gray-900">{c.case_title}</p>
                  <p className="text-xs text-gray-500">{c.client_name} · {c.case_type}</p>
                </div>
                <StatusBadge status={c.status} />
              </div>
            )) : (
              <div className="text-center text-gray-400 text-sm py-8">No cases yet</div>
            )}
          </div>
        </div>

        {/* My Tasks (non-management) or Upcoming Events (management/secretary) */}
        {!isManagement && role !== 'Secretary' ? (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">My Pending Tasks</h3>
              <button onClick={() => navigate('/tasks')} className="text-blue-600 text-sm hover:underline">View all</button>
            </div>
            <div className="space-y-2">
              {data?.my_tasks?.length > 0 ? data.my_tasks.map(t => (
                <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.task_name}</p>
                    <p className="text-xs text-gray-500">{t.case_title || 'No case'} · Due {t.due_date || '—'}</p>
                  </div>
                  <StatusBadge status={t.priority} />
                </div>
              )) : (
                <div className="text-center text-gray-400 text-sm py-8">No pending tasks</div>
              )}
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Upcoming Events</h3>
              <button onClick={() => navigate('/calendar')} className="text-blue-600 text-sm hover:underline">View calendar</button>
            </div>
            <div className="space-y-3">
              {data?.upcoming_events?.length > 0 ? data.upcoming_events.map(ev => (
                <div key={ev.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: ev.color || '#3b82f6' }}>
                    <span className="text-white text-xs font-bold leading-none">
                      {new Date(ev.start_datetime).getDate()}
                    </span>
                    <span className="text-white/80 text-xs leading-none">
                      {new Date(ev.start_datetime).toLocaleString('en', { month: 'short' })}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{ev.title}</p>
                    <p className="text-xs text-gray-500">{ev.event_type} · {new Date(ev.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    {ev.location && <p className="text-xs text-gray-400">{ev.location}</p>}
                  </div>
                </div>
              )) : (
                <div className="text-center text-gray-400 text-sm py-8">No upcoming events</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Accountant: billing summary row */}
      {isBilling && !isManagement && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Revenue — Last 6 Months</h3>
            {data?.monthly_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.monthly_revenue}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [`$${v.toLocaleString()}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No revenue data yet</div>
            )}
          </div>
          <div className="card">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Upcoming Events</h3>
            <div className="space-y-3">
              {data?.upcoming_events?.length > 0 ? data.upcoming_events.map(ev => (
                <div key={ev.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: ev.color || '#3b82f6' }}>
                    <span className="text-white text-xs font-bold leading-none">
                      {new Date(ev.start_datetime).getDate()}
                    </span>
                    <span className="text-white/80 text-xs leading-none">
                      {new Date(ev.start_datetime).toLocaleString('en', { month: 'short' })}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{ev.title}</p>
                    <p className="text-xs text-gray-500">{ev.event_type} · {new Date(ev.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              )) : (
                <div className="text-center text-gray-400 text-sm py-8">No upcoming events</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
